import { CircularProgressbarWithChildren } from 'react-circular-progressbar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  usePageInfo,
  useTeamRefreshStatus,
} from 'app/frontend/base/config/queries'
import { teamRefreshStatusTypes } from 'app/frontend/types/custom/TeamRefreshStatus'
import { CircleQuestionMark, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import Timestamp from 'app/frontend/base/components/Timestamp'
import { Button } from '@/components/ui/button'
import { useUpdateTeam } from 'app/frontend/base/config/mutations'
import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { useQueryClient } from '@tanstack/react-query'
import Card from 'app/frontend/base/components/Card'

type Props = {
  type: typeof teamRefreshStatusTypes[number]
}

const RefreshStatusBlock = ({ type }: Props) => {
  const { team, guild } = useTeam()
  const {
    data: { user },
  } = usePageInfo()
  const { t } = useTranslation()
  const { data } = useTeamRefreshStatus(team.id)
  const queryClient = useQueryClient()
  const { mutate } = useUpdateTeam<
    wowaudit.Team & { refresh_type: typeof teamRefreshStatusTypes[number] }
  >(
    team,
    () => {
      queryClient.invalidateQueries({
        queryKey: ['teamRefreshStatus', { id: team.id }],
      })
    },
    () => {
      setRefreshingManually(false)
    },
  )
  const [refreshingManually, setRefreshingManually] = useState(false)

  const lastRefreshed = {
    collections: team.lastRefreshedCollections,
    raiderio: team.lastRefreshedRaiderio,
    wcl: team.lastRefreshedWcl,
  }[type]

  const status = data?.status[type]

  const handleRefreshNow = () => {
    setRefreshingManually(true)
    mutate({ refresh_type: type })
  }

  useEffect(() => {
    setRefreshingManually(lastRefreshed === 0)
  }, [lastRefreshed])

  return (
    <Card>
      <h4 className='flex items-center justify-center gap-2'>
        {t(`dashboard.team.spreadsheet.status.${type}.title`)}
        <Tooltip>
          <TooltipTrigger>
            <CircleQuestionMark size={14} />
          </TooltipTrigger>
          <TooltipContent>
            {t(`dashboard.team.spreadsheet.status.${type}.description`)}
          </TooltipContent>
        </Tooltip>
      </h4>

      {status?.progress ? (
        <CircularProgressbarWithChildren
          strokeWidth={10}
          className='p-8 -mt-4'
          value={status.progress * 100}
          styles={{
            text: {
              fill: 'var(--color-muted-foreground)',
            },
            path: {
              stroke: 'var(--color-primary)',
              transition: 'stroke-dashoffset 2s ease 0s',
              strokeLinecap: 'butt',
            },
            trail: {
              stroke: 'var(--color-background)',
            },
          }}
        >
          <div className='flex flex-col items-center justify-center'>
            <div className='text-muted-foreground'>Progress</div>
            <div className='text-2xl font-semibold'>
              {status.progress ? `${Math.round(status.progress * 100)}%` : '0%'}
            </div>
          </div>
        </CircularProgressbarWithChildren>
      ) : (
        <div className='flex items-center justify-center pb-[1.1rem]'>
          <Loader2 size={180} className='animate-spin text-background' />
        </div>
      )}

      <div className='flex flex-col gap-4 items-center'>
        <div className='flex flex-col gap-0.5 items-center'>
          <Label className='text-muted-foreground'>Last refreshed</Label>
          <div
            className={cn(
              'font-semibold transition-opacity',
              status ? 'opacity-100' : 'opacity-0',
            )}
          >
            {status?.last_refresh ? (
              <Timestamp time={status.last_refresh} markOutdatedDays={null} />
            ) : (
              '-'
            )}
          </div>
        </div>

        <div className='flex flex-col gap-0.5 items-center'>
          <Label className='text-muted-foreground'>Position in queue</Label>
          <div
            className={cn(
              'font-semibold transition-opacity',
              status ? 'opacity-100' : 'opacity-0',
            )}
          >
            {status?.position ?? 'N/A'}
          </div>
        </div>

        <div className='flex flex-col gap-0.5 items-center'>
          <Label className='text-muted-foreground'>
            Estimated next refresh
          </Label>
          <div
            className={cn(
              'font-semibold transition-opacity',
              status ? 'opacity-100' : 'opacity-0',
            )}
          >
            {status?.estimate ? (
              <Timestamp time={status.estimate} markOutdatedDays={null} />
            ) : (
              'in progress'
            )}
          </div>
        </div>

        {user?.canRefreshManually || guild?.canRefreshManually ? (
          <Button
            size='sm'
            onClick={handleRefreshNow}
            loading={refreshingManually || (status && !status?.estimate)}
            spinner='blizzard'
            variant='outline'
          >
            Refresh now
          </Button>
        ) : (
          <Tooltip>
            <TooltipTrigger className='cursor-not-allowed'>
              <Button size='sm' variant='outline' disabled>
                Refresh now
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Refreshing on demand is only available for users with a
              subscription tier of at least{' '}
              <span className='font-semibold text-quality-epic'>Epic</span>.
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </Card>
  )
}

export default RefreshStatusBlock
